import React from 'react';
import styled from 'styled-components';
import { VisuallyHidden } from '@ui';
import { ColumnChooser } from './ColumnChooser';
import { ColumnsRefusedNotice } from './ColumnsRefusedNotice';
import { DrillDownBreadcrumb } from './DrillDownBreadcrumb';
import { FeedPrompt } from './FeedPrompt';
import { FeedRowsTable } from './FeedRowsTable';
import { LoadColumnsButton } from './LoadColumnsButton';

// Everything below the query: where you are, what produced these numbers, what was asked for, and
// the rows themselves.
//
// The page held this inline, which is why "restyle the results area" and "change how a read is
// issued" were edits to one file. The region is focusable as a unit — a completed read moves focus
// here rather than leaving the analyst to notice that something appeared below the form — and that
// is a property of the region, so it comes with it.

const SectionDivider = styled.div`
  border-top: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  margin-top: 16px;
  padding-top: 16px;
`;

const RESULTS_ID = 'feed-view-results';

export const FeedResultsPanel = ({
  result,
  displayedFeedId,
  isSuperseded,
  canReadSpecs,
  stack,
  onNavigate,
  hiddenColumns,
  onToggleColumn,
  columnsRefused,
  refusedColumnNames,
  onAskForColumnsAgain,
  canLoadColumns,
  loadColumnsLabel,
  onLoadColumns,
  onDrillDown,
  canDrillDown,
  resultsRef,
}) => (
  <SectionDivider>
    {/* Focusable so a completed read can move focus here; the row count inside the table is a live
        region, so the count is announced rather than the whole grid. */}
    <div id={RESULTS_ID} ref={resultsRef} tabIndex={-1} aria-labelledby={`${RESULTS_ID}-label`}>
      <VisuallyHidden id={`${RESULTS_ID}-label`}>Feed results</VisuallyHidden>
      {/* Both of these describe the rows below them, so both key on the DISPLAYED feed rather than
          the one a request was last sent for. On the pending feed the prompt panel stated a failed
          feed's question above another feed's scores — which is the one thing that panel exists not
          to do. There is no redundant "A > A" while a drill-down read is in flight either: the
          parent is pushed when the member's rows land, so during the read the trail says exactly
          what is still true. */}
      <DrillDownBreadcrumb stack={stack} currentFeedId={displayedFeedId} onNavigate={onNavigate} />
      <FeedPrompt feedId={displayedFeedId} canReadSpecs={canReadSpecs} />
      {columnsRefused && (
        <ColumnsRefusedNotice
          result={result}
          feedId={displayedFeedId}
          refusedColumnNames={refusedColumnNames}
          onAskAgain={onAskForColumnsAgain}
        />
      )}
      <ColumnChooser result={result} hiddenColumns={hiddenColumns} onToggle={onToggleColumn} />
      {canLoadColumns && <LoadColumnsButton label={loadColumnsLabel} onLoad={onLoadColumns} />}
      <FeedRowsTable
        result={result}
        feedId={displayedFeedId}
        isSuperseded={isSuperseded}
        hiddenColumns={hiddenColumns}
        onDrillDown={onDrillDown}
        canDrillDown={canDrillDown}
      />
    </div>
  </SectionDivider>
);
